import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getOpportunityMonitoring } from "../api/client.js";
import { MultiLineChart } from "../components/charts.jsx";
import { Shell } from "../components/Shell.jsx";
import {
  ErrorCard,
  Loading,
  MetricValue,
  MissingEvidenceState,
  Panel,
  StatTile,
  StatusPill,
  formatCount,
  formatPct,
} from "../components/ui.jsx";
import { DOMAIN_LABELS, sortActionsByStage } from "../domainModel.js";
import { getActionDetection } from "../opportunityDetection.js";
import {
  OUTCOME_EVIDENCE_UNAVAILABLE,
  deliveryEvidenceState,
  publicationState,
} from "../campaignLifecycleContract.js";
import { ACTIVATION_PATHS, campaignRoute, monitoringRoute } from "../navConfig.js";

function monthLabel(value, long = false) {
  if (!value) return "Unavailable";
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: long ? "long" : "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

function formatCurrency(value) {
  if (value == null) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function changeText(current, previous, previousMonth) {
  if (!previous) return "First available snapshot";
  if (current == null || previous.value == null) {
    return `Comparison with ${monthLabel(previousMonth)} is unavailable`;
  }
  const change = current - previous.value;
  if (change === 0) return `No change from ${monthLabel(previousMonth)}`;
  return `${change > 0 ? "+" : ""}${formatCount(change)} from ${monthLabel(previousMonth)}`;
}

function metricChange(rows, key) {
  if (rows.length < 2) return null;
  return { value: rows[rows.length - 2][key] };
}

function seriesValues(rows, months, key) {
  const byMonth = new Map(rows.map((row) => [row.runMonth, row]));
  return months.map((month) => byMonth.get(month)?.[key] ?? null);
}

function belowMinimumSummary(rows, months, keys) {
  const byMonth = new Map(rows.map((row) => [row.runMonth, row]));
  const loadedRows = months.map((month) => byMonth.get(month)).filter(Boolean);
  const affectedMonths = loadedRows.filter((row) =>
    keys.some((key) => row.belowMinCohortMetrics?.includes(key))
  );

  if (!affectedMonths.length) return null;
  return {
    loadedMonthCount: loadedRows.length,
    affectedMonthCount: affectedMonths.length,
  };
}

function hasTrend(chart) {
  const representedPeriods = chart.series[0]?.values.map((_, index) =>
    chart.series.some((series) => series.values[index] != null)
  );
  return representedPeriods?.filter(Boolean).length >= 2;
}

function isDemoCampaign(deployment) {
  return Boolean(deployment.syntheticTimeline || deployment.syntheticEffectiveness);
}

function RowMetric({ row, valueKey, minimum, grain, unavailableReason }) {
  const suppressed = row?.suppressedMetrics?.includes(valueKey);
  return (
    <MetricValue
      value={row?.[valueKey]}
      grain={grain}
      suppressed={suppressed}
      minimum={minimum}
      unavailableReason={unavailableReason}
    />
  );
}

const MOVEMENT_TYPES = [
  { key: "new", label: "Newly detected", note: "Appeared in this evidence period" },
  { key: "continued", label: "Continued", note: "Remained detected across periods" },
  {
    key: "resolved",
    label: "No longer detected",
    note: "Signal changed; this is not a campaign outcome",
  },
  { key: "stage_changed", label: "Stage changed", note: "Moved to a different clinical stage" },
];
const CAMPAIGN_TYPE_LABELS = {
  goal1_enrollment: "Enrollment campaign",
  goal2_usage: "Active usage campaign",
};
const MONITORING_DISPLAY_MONTHS = 12;

function campaignTypeLabel(campaign) {
  return CAMPAIGN_TYPE_LABELS[campaign.activationGoal] || "Other campaign";
}

function trailingMonthRange(endMonth, count = MONITORING_DISPLAY_MONTHS) {
  if (!endMonth) return [];
  const [endYear, endNumber] = endMonth.split("-").map(Number);
  const absoluteEnd = endYear * 12 + endNumber - 1;
  return Array.from({ length: count }, (_, index) => {
    const absolute = absoluteEnd - count + index + 1;
    const year = Math.floor(absolute / 12);
    return `${year}-${String((absolute % 12) + 1).padStart(2, "0")}`;
  });
}

export function OpportunityMonitoring({ tenant, enums, params, auth }) {
  const requestedDomain = params.get("programDomain") || "";
  const requestedRunMonth = params.get("runMonth") || "";
  const [data, setData] = useState(null);
  const [domain, setDomain] = useState(requestedDomain);
  const [selectedRunMonth, setSelectedRunMonth] = useState(requestedRunMonth);
  const [explorerActions, setExplorerActions] = useState(null);
  const [selectedAction, setSelectedAction] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    getOpportunityMonitoring()
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch(setError);
  }, []);

  useEffect(() => {
    if (!data?.programs.length) return;
    if (requestedDomain && data.programs.includes(requestedDomain)) {
      setDomain(requestedDomain);
    } else if (!data.programs.includes(domain)) {
      setDomain(data.programs[0]);
    }
  }, [data, domain, requestedDomain]);

  useEffect(() => {
    if (!data || !domain) return;
    const actions = Array.from(
      new Set(
        (data.opportunityActionMonthly || [])
          .filter((row) => row.programDomain === domain && row.recommendedAction)
          .map((row) => row.recommendedAction)
      )
    ).map((label) => ({ label }));
    setExplorerActions(actions);
    const ordered = sortActionsByStage(enums, domain, actions).map((row) => row.label);
    const demoTarget = (data.campaignDeployments || []).find(
      (campaign) => campaign.syntheticEffectiveness && campaign.programDomain === domain
    )?.targetOpportunities?.[0];
    const preferred = ordered.includes(demoTarget) ? demoTarget : ordered[0] || "";
    setSelectedAction((current) => (ordered.includes(current) ? current : preferred));
  }, [data, domain, enums]);

  const rows = useMemo(
    () =>
      (data?.monthly || [])
        .filter((row) => row.programDomain === domain)
        .sort((left, right) => left.runMonth.localeCompare(right.runMonth)),
    [data, domain]
  );
  const windowEndRows = useMemo(() => {
    const historyMonths = data?.displayMonths || [];
    const validEndMonths = new Set(historyMonths.slice(MONITORING_DISPLAY_MONTHS - 1));
    return rows.filter((row) => validEndMonths.has(row.runMonth));
  }, [data, rows]);
  useEffect(() => {
    if (!windowEndRows.length) {
      setSelectedRunMonth("");
      return;
    }
    const requestedIsAvailable = windowEndRows.some((row) => row.runMonth === requestedRunMonth);
    setSelectedRunMonth((current) => {
      const next = requestedIsAvailable
        ? requestedRunMonth
        : windowEndRows.some((row) => row.runMonth === current)
          ? current
          : windowEndRows.at(-1).runMonth;
      return current === next ? current : next;
    });
  }, [requestedRunMonth, windowEndRows]);

  useEffect(() => {
    if (!domain || !selectedRunMonth) return;
    window.history.replaceState(
      null,
      "",
      monitoringRoute({ programDomain: domain, runMonth: selectedRunMonth })
    );
  }, [domain, selectedRunMonth]);
  const deployments = useMemo(
    () =>
      (data?.campaignDeployments || [])
        .filter((row) => row.programDomain === domain)
        .filter((row) => !row.deploymentMonth || row.deploymentMonth <= selectedRunMonth)
        .sort((left, right) => String(right.deployedAt || "").localeCompare(String(left.deployedAt || ""))),
    [data, domain, selectedRunMonth]
  );
  const actionTimelineMonths = useMemo(() => trailingMonthRange(selectedRunMonth), [selectedRunMonth]);

  if (error) {
    return (
      <Shell tenant={tenant} auth={auth} activeNav="monitoring" title="Monitoring">
        <ErrorCard error={error} />
      </Shell>
    );
  }
  if (!data || !domain || explorerActions === null) {
    return (
      <Shell tenant={tenant} auth={auth} activeNav="monitoring" title="Monitoring">
        <Loading />
      </Shell>
    );
  }

  const selectedIndex = rows.findIndex((row) => row.runMonth === selectedRunMonth);
  const rowsInWindow = rows.filter((row) => actionTimelineMonths.includes(row.runMonth));
  const selected = selectedIndex >= 0 ? rows[selectedIndex] : null;
  const previous = rowsInWindow.length > 1 ? rowsInWindow.at(-2) : null;
  const viewingLatest = selected?.runMonth === rows.at(-1)?.runMonth;
  const orderedOpportunityActions = sortActionsByStage(enums, domain, explorerActions).map(
    (row) => row.label
  );
  const actionRows = (data.opportunityActionMonthly || [])
    .filter(
      (row) =>
        row.programDomain === domain &&
        row.recommendedAction === selectedAction &&
        actionTimelineMonths.includes(row.runMonth)
    )
    .sort((left, right) => left.runMonth.localeCompare(right.runMonth));
  const actionImpactCampaigns = deployments.filter(
    (deployment) =>
      deployment.syntheticEffectiveness && deployment.targetOpportunities?.includes(selectedAction)
  );
  const actionImpactMarkers = actionImpactCampaigns.flatMap((campaign) => [
    {
      key: `${campaign.campaignId}-launch`,
      month: campaign.deploymentMonth,
      label: `${campaign.campaignName} launched`,
    },
    {
      key: `${campaign.campaignId}-impact`,
      month: campaign.outcomePeriod,
      label: `${campaign.campaignName} claims impact begins`,
    },
  ]);
  const programImpactCampaigns = deployments.filter(
    (campaign) => !campaign.deploymentMonth || campaign.deploymentMonth <= selectedRunMonth
  );
  const programImpactMarkers = programImpactCampaigns.flatMap((campaign) => {
    const type = campaignTypeLabel(campaign);
    return [
      {
        key: `${campaign.campaignId}-program-launch`,
        month: campaign.deploymentMonth,
        label: `${campaign.campaignName} · ${type} · launched`,
      },
      ...(campaign.syntheticEffectiveness && campaign.outcomePeriod
        ? [
            {
              key: `${campaign.campaignId}-program-impact`,
              month: campaign.outcomePeriod,
              label: `${campaign.campaignName} · ${type} · claims impact begins`,
            },
          ]
        : []),
    ];
  });
  const actionDetection = selectedAction ? getActionDetection(domain, selectedAction) : null;
  const opportunityCharts = selected
    ? [
        ...(data.demoEffectiveness?.active
          ? [
              {
                key: "program-campaign-impact",
                title: `${DOMAIN_LABELS[domain] || domain} campaign impact`,
                markers: programImpactMarkers,
                markerLabel: "Campaign launch / claims impact",
                campaigns: programImpactCampaigns,
                definition: {
                  opportunity:
                    "The program-level population with a current opportunity signal after the demo date projection.",
                  actionable:
                    "Ready-for-outreach opportunities after subtracting the modeled campaign impact across targeted actions.",
                },
                series: [
                  {
                    key: "program-opportunity",
                    label: "Opportunity population",
                    values: seriesValues(rowsInWindow, actionTimelineMonths, "distinctMembers"),
                  },
                  {
                    key: "program-actionable",
                    label: "Ready after campaign impact",
                    values: seriesValues(rowsInWindow, actionTimelineMonths, "actionableMembers"),
                  },
                  {
                    key: "program-impact",
                    label: "Modeled opportunity reduction",
                    color: "#C8D322",
                    values: seriesValues(
                      rowsInWindow,
                      actionTimelineMonths,
                      "syntheticCampaignImpactMembers"
                    ),
                  },
                ],
              },
            ]
          : []),
        ...(selectedAction
          ? [
              {
                key: "selected-action-trend",
                title: selectedAction,
                selectable: true,
                definition: {
                  opportunity: actionDetection.detection,
                  actionable: `${actionDetection.opportunity} Included when the opportunity is active, top-ranked, and not suppressed.`,
                },
                belowMinimum: belowMinimumSummary(actionRows, actionTimelineMonths, [
                  "opportunityMembers",
                  "actionableMembers",
                  "suppressedMembers",
                ]),
                markers: actionImpactMarkers,
                markerLabel: "Demo campaign launch / claims impact",
                series: [
                  {
                    key: "opportunity",
                    label: "Covered people with opportunity",
                    values: seriesValues(actionRows, actionTimelineMonths, "opportunityMembers"),
                  },
                  {
                    key: "actionable",
                    label: "Ready for outreach",
                    values: seriesValues(actionRows, actionTimelineMonths, "actionableMembers"),
                  },
                  {
                    key: "suppressed",
                    label: "Held or routed to review",
                    values: seriesValues(actionRows, actionTimelineMonths, "suppressedMembers"),
                  },
                  {
                    key: "campaign-impact",
                    label: "Modeled opportunity reduction",
                    color: "#C8D322",
                    values: seriesValues(actionRows, actionTimelineMonths, "syntheticCampaignImpactMembers"),
                  },
                ],
              },
            ]
          : []),
      ]
    : [];
  const selectedTransitions = (data.transitions || []).filter(
    (transition) => transition.programDomain === domain && transition.runMonth === selected?.runMonth
  );
  const transitionByType = new Map(
    selectedTransitions.map((transition) => [transition.transitionType, transition])
  );
  const availableWindowMonths = new Set(rowsInWindow.map((row) => row.runMonth));
  const missingMonthsThroughSelection = actionTimelineMonths.filter(
    (month) => !availableWindowMonths.has(month)
  );
  const effectivenessCampaigns = deployments
    .filter(
      (deployment) => deployment.syntheticEffectiveness && deployment.deploymentMonth <= selectedRunMonth
    )
    .map((campaign) => {
      const progress = (campaign.monthlyProgress || [])
        .filter((row) => row.runMonth <= selectedRunMonth)
        .at(-1);
      return progress ? { ...campaign, ...progress } : campaign;
    });
  const estimatedNetSavings = effectivenessCampaigns.reduce(
    (total, campaign) => total + Number(campaign.estimatedNetSavings || 0),
    0
  );

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="monitoring"
      routeContext={DOMAIN_LABELS[domain] || domain}
      title="Monitoring"
      subtitle="Opportunity movement, publication and delivery, and outcome evidence are reported separately"
      topbarControl={
        <label className="bi-topbar-period" htmlFor="monitoring-period">
          <span>As of</span>
          <select
            id="monitoring-period"
            className="bi-topbar-period-select"
            value={selectedRunMonth}
            onChange={(event) => setSelectedRunMonth(event.target.value)}
          >
            {[...windowEndRows].reverse().map((row) => (
              <option key={row.runMonth} value={row.runMonth}>
                {monthLabel(row.runMonth, true)}
              </option>
            ))}
          </select>
        </label>
      }
      headerRight={
        <Link className="bi-btn-ghost" to={ACTIVATION_PATHS.overview}>
          Activate overview
        </Link>
      }
    >
      <div className="monitoring-toolbar">
        <div className="opportunity-tabs" aria-label="Program">
          {data.programs.map((program) => (
            <Link
              key={program}
              className={`chip${program === domain ? " on" : ""}`}
              to={monitoringRoute({ programDomain: program, runMonth: selectedRunMonth })}
              aria-current={program === domain ? "page" : undefined}
            >
              {DOMAIN_LABELS[program] || program}
            </Link>
          ))}
        </div>
        <span className="monitoring-privacy-note">
          Program counts remain separate to prevent cross-program double counting.
        </span>
      </div>

      <section className="monitoring-lane opportunity-lane" aria-labelledby="opportunity-movement-title">
        <header className="monitoring-lane-heading">
          <span>Lane 1</span>
          <div>
            <h2 id="opportunity-movement-title">Opportunity movement</h2>
            <p>
              Monthly aggregate opportunity snapshots · Evidence-period basis · Source: approved analytical
              summaries
            </p>
          </div>
          <StatusPill
            status={selected ? (viewingLatest ? "current" : "available") : "missing"}
            label={selected ? (viewingLatest ? "Latest evidence" : "Historical evidence") : "Unavailable"}
          />
        </header>

        {data.demoEffectiveness?.active && data.demoEffectiveness.latestRunMonth && (
          <div className="demo-timeline-notice monitoring-demo-notice" role="note">
            <strong>Mock data window</strong>
            <span>
              Available aggregate history is shifted to show 24 monthly periods from{" "}
              {monthLabel(trailingMonthRange(data.demoEffectiveness.latestRunMonth, 24)[0], true)} through{" "}
              {monthLabel(data.demoEffectiveness.latestRunMonth, true)}. Mock campaign and claims-impact
              results are presentation-only demo data; each impact reduces its targeted opportunity series
              after the stated measurement lag.
            </span>
          </div>
        )}

        {missingMonthsThroughSelection.length > 0 && (
          <div className="monitoring-gap-note" role="status">
            <strong>
              {missingMonthsThroughSelection.length} missing evidence{" "}
              {missingMonthsThroughSelection.length === 1 ? "period" : "periods"}:
            </strong>
            {missingMonthsThroughSelection.map((month) => monthLabel(month)).join(", ")}. Charts leave these
            periods blank rather than treating them as zero.
          </div>
        )}

        {!selected ? (
          <MissingEvidenceState
            reason={`No opportunity snapshots are available for ${DOMAIN_LABELS[domain] || domain}.`}
            effect="Movement and comparison metrics cannot be calculated."
          />
        ) : (
          <>
            <div className="monitoring-stat-row">
              <StatTile
                k="As of"
                v={monthLabel(selected.runMonth, true)}
                d={`${rowsInWindow.length} monthly snapshots through this date`}
              />
              <StatTile
                k="Opportunity population"
                v={
                  <RowMetric
                    row={selected}
                    valueKey="distinctMembers"
                    minimum={data.minCohortSize}
                    grain="covered people"
                  />
                }
                d={changeText(
                  selected.distinctMembers,
                  metricChange(rowsInWindow, "distinctMembers"),
                  previous?.runMonth
                )}
              />
              <StatTile
                k="Ready for outreach"
                v={
                  <RowMetric
                    row={selected}
                    valueKey="actionableMembers"
                    minimum={data.minCohortSize}
                    grain="covered people"
                  />
                }
                d={changeText(
                  selected.actionableMembers,
                  metricChange(rowsInWindow, "actionableMembers"),
                  previous?.runMonth
                )}
              />
              <StatTile
                k="Held or routed to review"
                v={
                  <RowMetric
                    row={selected}
                    valueKey="suppressedMembers"
                    minimum={data.minCohortSize}
                    grain="covered people"
                  />
                }
                d={changeText(
                  selected.suppressedMembers,
                  metricChange(rowsInWindow, "suppressedMembers"),
                  previous?.runMonth
                )}
              />
              {data.demoEffectiveness?.active && (
                <StatTile
                  k="Modeled campaign reduction"
                  v={formatCount(selected.syntheticCampaignImpactMembers || 0)}
                  d={`${formatCurrency(estimatedNetSavings)} estimated net savings across all affected opportunity areas`}
                />
              )}
            </div>

            <Panel
              headingLevel={3}
              full
              title="Selected evidence-period movement"
              desc="Changes in opportunity signals, not campaign outcomes"
            >
              {selectedTransitions.length ? (
                <div className="monitoring-movement-summary">
                  {MOVEMENT_TYPES.map((movement) => {
                    const transition = transitionByType.get(movement.key);
                    return (
                      <div key={movement.key}>
                        <span>{movement.label}</span>
                        <MetricValue
                          value={transition?.memberCount}
                          grain="covered people"
                          suppressed={transition?.suppressed}
                          minimum={data.minCohortSize}
                          unavailableReason="No transition summary is available"
                        />
                        <small>{movement.note}</small>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <MissingEvidenceState
                  reason="No prior-period transition summary is available."
                  effect="The selected snapshot can be shown, but movement cannot be classified."
                />
              )}
            </Panel>

            <div className="monitoring-grid">
              {opportunityCharts.map((chart) => (
                <Panel
                  key={chart.key}
                  headingLevel={3}
                  title={
                    chart.selectable ? (
                      <select
                        id="monitoring-action"
                        className="monitoring-chart-title-select"
                        aria-label="Recommended action trend"
                        value={selectedAction}
                        onChange={(event) => setSelectedAction(event.target.value)}
                      >
                        {orderedOpportunityActions.map((action) => (
                          <option key={action}>{action}</option>
                        ))}
                      </select>
                    ) : (
                      chart.title
                    )
                  }
                >
                  {hasTrend(chart) ? (
                    <MultiLineChart
                      title={`${chart.title} by evidence period`}
                      labels={actionTimelineMonths}
                      format={formatCount}
                      series={chart.series}
                      markers={chart.markers}
                      markerLabel={chart.markerLabel}
                      showDataTable
                    />
                  ) : (
                    <MissingEvidenceState
                      reason="Fewer than two evidence periods contain comparable values."
                      effect="A trend is not shown until another comparable snapshot is available."
                    />
                  )}
                  {chart.campaigns?.length > 0 && (
                    <section
                      className="monitoring-chart-campaigns"
                      aria-label="Campaigns represented in this chart"
                    >
                      <h4>Campaigns in this view</h4>
                      <ul>
                        {chart.campaigns.map((campaign) => (
                          <li key={`${campaign.campaignId}-${campaign.campaignVersion || 1}`}>
                            <div>
                              <strong>{campaign.campaignName}</strong>
                              <span>{campaignTypeLabel(campaign)}</span>
                            </div>
                            <small>
                              Launched {monthLabel(campaign.deploymentMonth)} ·{" "}
                              {campaign.syntheticEffectiveness
                                ? "Modeled claims impact"
                                : "Launch event only"}
                            </small>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {chart.belowMinimum && (
                    <div className="chart-minimum-note" role="note">
                      Values below {data.minCohortSize} covered people are suppressed in{" "}
                      {chart.belowMinimum.affectedMonthCount} of {chart.belowMinimum.loadedMonthCount} loaded
                      periods. Suppressed values are not treated as zero and cannot form a campaign cohort.
                    </div>
                  )}
                  <details className="chart-definition-disclosure">
                    <summary>Definitions</summary>
                    <div className="chart-definition-list">
                      <div>
                        <strong>Opportunity</strong>
                        <span>{chart.definition.opportunity}</span>
                      </div>
                      <div>
                        <strong>Ready</strong>
                        <span>{chart.definition.actionable}</span>
                      </div>
                    </div>
                  </details>
                </Panel>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="monitoring-lane operations-lane" aria-labelledby="publication-delivery-title">
        <header className="monitoring-lane-heading">
          <span>Lane 2</span>
          <div>
            <h2 id="publication-delivery-title">Publication and delivery</h2>
            <p>
              Campaign publication records and confirmed-contact summaries · Event timestamp basis · Source:
              governed campaign operations
            </p>
          </div>
          <StatusPill
            status={deployments.length ? "available" : "not_started"}
            label={deployments.length ? "Operational records available" : "No publication records"}
          />
        </header>

        <Panel full headingLevel={3} title={`${DOMAIN_LABELS[domain] || domain} campaign operations`}>
          {deployments.length ? (
            <div
              className="table-scroll"
              tabIndex={0}
              role="region"
              aria-label="Campaign publication and delivery status"
            >
              <table className="ae-table monitoring-operations-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Publication</th>
                    <th>Published audience</th>
                    <th>Delivery evidence</th>
                    <th>Published</th>
                  </tr>
                </thead>
                <tbody>
                  {deployments.map((deployment) => {
                    const demo = isDemoCampaign(deployment);
                    const deliveryEvidence = deliveryEvidenceState(deployment);
                    return (
                      <tr key={`${deployment.campaignId}-${deployment.campaignVersion}`}>
                        <td>
                          {deployment.syntheticEffectiveness && !deployment.sampleData ? (
                            <strong>{deployment.campaignName}</strong>
                          ) : (
                            <Link
                              className="ae-row-link"
                              to={campaignRoute(deployment.campaignId, {
                                from: "monitoring",
                                programDomain: domain,
                              })}
                            >
                              {deployment.campaignName}
                            </Link>
                          )}
                          {demo && (
                            <span className="monitoring-demo-marker">Demo campaign · synthetic data</span>
                          )}
                        </td>
                        <td>
                          <StatusPill {...publicationState(deployment.publicationStatus)} />
                        </td>
                        <td>
                          <RowMetric
                            row={deployment}
                            valueKey="publishedAudienceMembers"
                            minimum={data.minCohortSize}
                            grain="covered people"
                            unavailableReason="Published audience is not available"
                          />
                        </td>
                        <td>
                          {deliveryEvidence.kind === "available" ? (
                            <div className="monitoring-delivery-value">
                              <StatusPill status={deliveryEvidence.status} label={deliveryEvidence.label} />
                              <RowMetric
                                row={deployment}
                                valueKey="contactedMembers"
                                minimum={data.minCohortSize}
                                grain="covered people"
                              />
                            </div>
                          ) : deliveryEvidence.kind === "suppressed" ? (
                            <MetricValue
                              suppressed
                              minimum={data.minCohortSize}
                              grain="covered people with confirmed contact"
                            />
                          ) : (
                            <div className="monitoring-unavailable-cell">
                              <StatusPill status="unavailable" label="Unavailable" />
                              <small>{deliveryEvidence.reason}</small>
                            </div>
                          )}
                        </td>
                        <td>
                          {deployment.deployedAt
                            ? new Date(deployment.deployedAt).toLocaleString()
                            : "Unavailable"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <MissingEvidenceState
              reason={`No campaign publication records are available for ${DOMAIN_LABELS[domain] || domain}.`}
              effect="Delivery status cannot be inferred from campaign setup or approval."
            />
          )}
          <p className="monitoring-lane-note">
            {data.demoEffectiveness?.active
              ? "Rows labeled Demo campaign use synthetic delivery evidence and do not represent real contacts."
              : "A published definition is an authorized handoff only. Delivery remains unavailable until confirmed contact records are received."}
          </p>
        </Panel>
      </section>

      <section className="monitoring-lane outcomes-lane" aria-labelledby="outcome-evidence-title">
        <header className="monitoring-lane-heading">
          <span>Lane 3</span>
          <div>
            <h2 id="outcome-evidence-title">Claims impact</h2>
            <p>
              {data.demoEffectiveness?.active
                ? "Synthetic claims impact 2–3 months after launch · Demo comparison method"
                : "Validated post-contact outcomes · Outcome maturity basis · Source: no approved outcome feed is connected"}
            </p>
          </div>
          <StatusPill
            status={effectivenessCampaigns.length ? "available" : OUTCOME_EVIDENCE_UNAVAILABLE.status}
            label={
              effectivenessCampaigns.length ? "Synthetic demo results" : OUTCOME_EVIDENCE_UNAVAILABLE.label
            }
          />
        </header>
        <Panel full headingLevel={3} title="Campaign claims-impact measurement">
          {effectivenessCampaigns.length ? (
            <>
              <div className="demo-effectiveness-notice" role="note">
                <strong>Demo mode is on</strong>
                <span>
                  Every result below is synthetic and intended only to demonstrate claims-impact reporting. It
                  is not evidence of real delivery, engagement, or claims outcomes.
                </span>
              </div>
              <div className="demo-effectiveness-grid">
                {effectivenessCampaigns.map((campaign) => (
                  <article className="demo-effectiveness-card" key={campaign.campaignId}>
                    <header>
                      <div>
                        <span>{DOMAIN_LABELS[campaign.programDomain] || campaign.programDomain}</span>
                        <h3>{campaign.campaignName}</h3>
                      </div>
                      <StatusPill status="available" label={campaign.phase || "Synthetic result"} />
                    </header>
                    <p>
                      Progress through {monthLabel(selectedRunMonth, true)} · launched{" "}
                      {monthLabel(campaign.deploymentMonth, true)} · claims impact begins after{" "}
                      {campaign.impactLagMonths} months in {monthLabel(campaign.outcomePeriod, true)}
                    </p>
                    <dl className="demo-effectiveness-metrics">
                      <div>
                        <dt>Confirmed contacts</dt>
                        <dd>{formatCount(campaign.contactedMembers)}</dd>
                      </div>
                      <div>
                        <dt>Engagement rate</dt>
                        <dd>{formatPct(campaign.engagementRate)}</dd>
                      </div>
                      <div>
                        <dt>Opportunities reduced</dt>
                        <dd>{formatCount(campaign.affectedOpportunityMembers)}</dd>
                      </div>
                      <div>
                        <dt>{campaign.outcomeLabel}</dt>
                        <dd>{formatPct(campaign.outcomeRate)}</dd>
                      </div>
                      <div>
                        <dt>Comparison claims rate</dt>
                        <dd>{formatPct(campaign.comparisonRate)}</dd>
                      </div>
                      <div className="demo-effectiveness-lift">
                        <dt>Estimated rate lift</dt>
                        <dd>+{formatPct(campaign.absoluteLift)}</dd>
                      </div>
                      <div>
                        <dt>Gross savings</dt>
                        <dd>{formatCurrency(campaign.estimatedGrossSavings)}</dd>
                      </div>
                      <div>
                        <dt>Campaign cost</dt>
                        <dd>{formatCurrency(campaign.estimatedCampaignCost)}</dd>
                      </div>
                      <div className="demo-effectiveness-lift">
                        <dt>Estimated net savings</dt>
                        <dd>{formatCurrency(campaign.estimatedNetSavings)}</dd>
                      </div>
                      <div>
                        <dt>Estimated ROI</dt>
                        <dd>{formatPct(campaign.estimatedRoi)}</dd>
                      </div>
                    </dl>
                    <small>
                      Synthetic demo method · aggregate comparison · {formatCount(campaign.outcomeMembers)} of{" "}
                      {formatCount(campaign.outcomeEligibleMembers)} eligible people had the example favorable
                      claims outcome. Savings assume{" "}
                      {formatCurrency(campaign.estimatedSavingsPerAffectedMember)}
                      per affected opportunity. Campaign cost assumes{" "}
                      {formatCurrency(campaign.fixedCampaignCost)}
                      fixed plus {formatCurrency(campaign.contactCostPerMember)} per confirmed contact.
                    </small>
                    <details className="chart-data-disclosure">
                      <summary>Opportunity areas affected</summary>
                      <div
                        className="chart-data-scroll"
                        tabIndex={0}
                        role="region"
                        aria-label="Opportunity areas affected by demo campaign"
                      >
                        <table className="chart-data-table">
                          <thead>
                            <tr>
                              <th>Recommended action</th>
                              <th>Baseline</th>
                              <th>Modeled reduction</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(campaign.opportunityImpactBreakdown || []).map((row) => (
                              <tr key={row.recommendedAction}>
                                <th scope="row">{row.recommendedAction}</th>
                                <td className="numeric">{formatCount(row.baselineOpportunityMembers)}</td>
                                <td className="numeric">
                                  {formatCount(row.projectedAffectedOpportunityMembers)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                    <details className="chart-data-disclosure">
                      <summary>Monthly progress</summary>
                      <div
                        className="chart-data-scroll"
                        tabIndex={0}
                        role="region"
                        aria-label="Monthly demo progress"
                      >
                        <table className="chart-data-table">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Phase</th>
                              <th>Contacts</th>
                              <th>Opportunities reduced</th>
                              <th>Net savings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(campaign.monthlyProgress || [])
                              .filter((row) => row.runMonth <= selectedRunMonth)
                              .map((row) => (
                                <tr key={row.runMonth}>
                                  <th scope="row">{monthLabel(row.runMonth)}</th>
                                  <td>{row.phase}</td>
                                  <td className="numeric">{formatCount(row.contactedMembers)}</td>
                                  <td className="numeric">{formatCount(row.affectedOpportunityMembers)}</td>
                                  <td className="numeric">{formatCurrency(row.estimatedNetSavings)}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <MissingEvidenceState
              reason={OUTCOME_EVIDENCE_UNAVAILABLE.reason}
              effect={OUTCOME_EVIDENCE_UNAVAILABLE.effect}
              action={
                <Link className="bi-btn-ghost" to={ACTIVATION_PATHS.overview}>
                  Return to Activate overview
                </Link>
              }
            />
          )}
        </Panel>
      </section>
    </Shell>
  );
}
